import React from 'react'
import styled from '@emotion/styled'
import { colors, font, spacing } from '@mobi/component-library/Theme/Common'
import { Last10WinLossIndicator } from '@core/Areas/BetLoop/Components/Common/Last10WinLossIndicator'
import { betLoopProfileStatsByIdQuery } from '@core/Areas/BetLoop/helpers/queries'
import { useQuery } from '@tanstack/react-query'
import { formatStatValue } from '@core/Areas/BetLoop/helpers/formatStatValue'
import { toMoney } from '@mobi/utils/money'

export const ProfileCardStats = ({ id }: { id: string }) => {
  const { data } = useQuery(betLoopProfileStatsByIdQuery(id))
  return (
    <StatsSectionStyled>
      <div>
        <Last10WinLossIndicator
          currentStreak={data?.currentWinStreak ?? 0}
          last10Results={data?.lastTenResults ?? []}
        />
      </div>

      <dl>
        <div>
          <dt>Followers</dt>
          <dd>{formatStatValue(data?.followerCount)}</dd>
        </div>

        <div>
          <dt>Copies</dt>
          <dd>{formatStatValue(data?.copyCount)}</dd>
        </div>

        <div>
          <dt>Shares</dt>
          <dd>{formatStatValue(data?.shareCount)}</dd>
        </div>

        <div>
          <dt>Avg. Odds</dt>
          <dd>{data?.avgOdds ? toMoney(data.avgOdds) : 'N/A'}</dd>
        </div>

        <div>
          <dt>Best Streak</dt>
          <dd>{data?.bestWinStreak}</dd>
        </div>

        <div>
          <dt>Tot. Wins</dt>
          <dd>{data?.totalWins}</dd>
        </div>
      </dl>
    </StatsSectionStyled>
  )
}

// ======
// Styles
// ======

const StatsSectionStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  fontFamily: font.family.primary,

  '> div:first-of-type': {
    marginBottom: '12%',
    paddingInline: '6%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignSelf: 'stretch',
  },

  '> dl': {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    rowGap: spacing.bigx1,
    margin: '0 -0.8rem 0 0.8rem',
    placeItems: 'center',

    '> div': {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.2rem',
      paddingLeft: spacing.smx1,
      width: '7rem',

      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        height: '100%',
        width: '3%',
        background: colors.neutral[400],
        transform: 'skew(-7deg)',
      },

      dt: {
        fontSize: font.size.sm.fontSize,
        letterSpacing: font.size.sm.letterSpacing,
        lineHeight: font.size.sm.lineHeight,
        fontWeight: font.weight.medium,
        fontStyle: 'italic',
        color: colors.neutral[700],
        margin: 0,
        padding: 0,
      },

      dd: {
        fontSize: font.size.xl3.fontSize,
        letterSpacing: font.size.xl3.letterSpacing,
        fontWeight: font.weight.black,
        lineHeight: 1,
        fontStyle: 'italic',
        color: colors.neutral[900],
        margin: 0,
        padding: 0,
      },
    },
  },
})
